import { useShallow } from "zustand/react/shallow";
import { CLOSED_TO_VISITORS_MESSAGE } from "@townsy/contracts";
import { useTownSession } from "../app/TownSessionContext";
import { propertyDisplayName } from "../lib/propertyLabels";
import { postureText, seatActionLabel } from "../net/seats";
import { useTownStore } from "../state/store";

/**
 * Bottom prompts in Walk mode on the street (one overlay layer so they never cover each other):
 *  - near a door: walking into the door enters (the server detects it and sends the transition,
 *    ADR-0004 #2); the Enter button stays as the accessible fallback (docs/01 section 7);
 *  - at a stopped car's driver's door: "Get in (E)" (ADR-0006; E, this button or a tap on the car);
 *  - while driving: the controls, so keyboard and touch users know how to steer and get out;
 *  - near a free seat, on the street or inside: "Sit on the sofa (E)" / "Lie down on the bed (E)" (ADR-0018), and while
 *    sitting or lying, what you are doing and a "Stand up" button (walking or E stand up too).
 * Town v6 (ADR-0042): the whole town is one street; there are no district gates to prompt for (Riverside and Hillcrest
 * are reached by walking or driving along Market Street).
 */
export interface BottomPromptInput {
  roomKind: "street" | "interior" | null;
  mode: "overview" | "walk";
  nearbyId: string | null;
  vehicleCandidateId: string | null;
  driving: boolean;
}

/** Which prompts show: doors and cars only on foot in Walk mode on the street; the driving help whenever driving. */
export function bottomPrompts(s: BottomPromptInput): { door: boolean; car: boolean; drivingHelp: boolean } {
  const street = s.roomKind === "street";
  const onFoot = street && s.mode === "walk" && !s.driving;
  return {
    door: onFoot && Boolean(s.nearbyId),
    car: onFoot && Boolean(s.vehicleCandidateId),
    drivingHelp: street && s.driving,
  };
}

export interface SeatPromptInput {
  roomKind: "street" | "interior" | null;
  mode: "overview" | "walk";
  driving: boolean;
  seatOffer: { seatId: string; kind: "sit" | "lie"; place: string } | null;
  seated: { kind: "sit" | "lie"; place: string | null } | null;
}

/**
 * ADR-0018: "Sit" / "Lie down" near a free seat (walk mode, on foot, standing; any room), and the "Stand up" prompt while
 * sitting or lying (in either camera mode, so the way back up is never hidden).
 */
export function seatPrompts(s: SeatPromptInput): { seat: boolean; seated: boolean } {
  const inRoom = s.roomKind !== null;
  const seated = inRoom && s.seated !== null;
  return { seat: inRoom && !seated && !s.driving && s.mode === "walk" && s.seatOffer !== null, seated };
}

export function EnterPrompt() {
  const session = useTownSession();
  const state = useTownStore(
    useShallow((s) => ({
      mode: s.ui.mode,
      roomKind: s.room.kind,
      nearbyId: s.ui.nearbyEntranceId,
      vehicleCandidateId: s.ui.vehicleCandidateId,
      driving: s.ui.drivingVehicleId !== null,
      vehiclePending: s.ui.vehiclePending,
      transitionPending: s.ui.transitionPending,
      isTouch: s.ui.isTouch,
      seatOffer: s.ui.seatOffer,
      seated: s.ui.seated,
      seatPending: s.ui.seatPending,
    })),
  );
  const show = bottomPrompts(state);
  const seats = seatPrompts(state);
  if (!show.door && !show.car && !show.drivingHelp && !seats.seat && !seats.seated) return null;
  // `.hud-bottom` is the bottom-centred overlay layer above the canvas; without it the prompt
  // would sit in normal flow underneath the absolutely positioned canvas and be unclickable.
  return (
    <div className="hud-bottom">
      {show.door && state.nearbyId ? <DoorPrompt nearbyId={state.nearbyId} /> : null}
      {show.car ? <GetInPromptView pending={state.vehiclePending} disabled={state.vehiclePending !== null || state.transitionPending || !session} onGetIn={() => session?.interactWithVehicle()} /> : null}
      {show.drivingHelp ? <DrivingHelpView isTouch={state.isTouch} /> : null}
      {seats.seat && state.seatOffer ? (
        <SeatPromptView
          offer={state.seatOffer}
          pending={state.seatPending}
          showKey={!state.isTouch}
          disabled={state.seatPending !== null || state.transitionPending || state.vehiclePending !== null || !session}
          onSit={() => session?.sitOn(state.seatOffer!.seatId)}
        />
      ) : null}
      {seats.seated && state.seated ? (
        <SeatedView seated={state.seated} pending={state.seatPending} isTouch={state.isTouch} disabled={state.seatPending === "stand" || !session} onStand={() => session?.standUp()} />
      ) : null}
    </div>
  );
}

/** Shown near a free seat (ADR-0018): "Sit on the sofa (E)" / "Lie down on the bed (E)". The server re-checks everything. */
export function SeatPromptView({
  offer,
  pending,
  showKey,
  disabled,
  onSit,
}: {
  offer: { kind: "sit" | "lie"; place: string };
  pending: "sit" | "lie" | "stand" | null;
  showKey: boolean;
  disabled: boolean;
  onSit(): void;
}) {
  const label = seatActionLabel(offer);
  return (
    <div className="enter-prompt" role="group" aria-label="Seat" data-testid="seat-prompt">
      <button
        type="button"
        className="btn btn-primary btn-lg"
        disabled={disabled}
        onClick={onSit}
        aria-label={pending === "sit" || pending === "lie" ? "Waiting for the seat" : `${label}${showKey ? " (E)" : ""}`}
        data-testid="seat-action"
      >
        {pending === "sit" ? "Sitting down…" : pending === "lie" ? "Lying down…" : `${label}${showKey ? " (E)" : ""}`}
      </button>
    </div>
  );
}

/** While sitting or lying (ADR-0018): what you are doing, and the way back up (walking or E also stand up). */
export function SeatedView({
  seated,
  pending,
  isTouch,
  disabled,
  onStand,
}: {
  seated: { kind: "sit" | "lie"; place: string | null };
  pending: "sit" | "lie" | "stand" | null;
  isTouch: boolean;
  disabled: boolean;
  onStand(): void;
}) {
  return (
    <div className="enter-prompt" role="group" aria-label="Posture" data-testid="seated-prompt">
      <span className="small" role="status" data-testid="posture-text">
        <strong>{postureText(seated.kind, seated.place)}</strong>
      </span>
      <span className="small muted">{isTouch ? "Move the joystick or tap Stand up to get up" : "Walk or press E to get up"}</span>
      <button type="button" className="btn btn-primary btn-lg" disabled={disabled} onClick={onStand} data-testid="stand-up">
        {pending === "stand" ? "Standing up…" : isTouch ? "Stand up" : "Stand up (E)"}
      </button>
    </div>
  );
}

function DoorPrompt({ nearbyId }: { nearbyId: string }) {
  const session = useTownSession();
  const { property, pending } = useTownStore(
    useShallow((s) => ({
      property: s.properties[nearbyId] ?? null,
      pending: s.ui.transitionPending,
    })),
  );
  const name = property ? (property.type === "COMMERCIAL" ? (property.placement?.businessName ?? property.address) : propertyDisplayName(property)) : nearbyId;
  // ADR-0019: the owner may enter their own closed home or store (the server admits them); everyone else sees "Closed"
  // and why (walking into the door gets the same answer from the server).
  const enterable = Boolean(property?.enterable || (session && session.canEnter(nearbyId)));
  const reason = property?.access === "closed" ? CLOSED_TO_VISITORS_MESSAGE : "This place is not open to visitors";
  return (
    <div className="enter-prompt" role="group" aria-label="Nearby entrance" data-testid="enter-prompt">
      <span className="small">
        <strong>{name}</strong>
      </span>
      <span className="small muted">{enterable ? "Walk through the door or press Enter" : reason}</span>
      <button
        type="button"
        className="btn btn-primary btn-lg"
        disabled={!enterable || pending || !session}
        onClick={() => void session?.enterProperty(nearbyId)}
        title={enterable ? "Enter (same as walking through the door)" : reason}
      >
        {pending ? "Entering…" : enterable ? "Enter" : "Closed"}
      </button>
    </div>
  );
}

/** Shown while the avatar stands at a stopped, free car's driver's door (the server re-checks on E). */
export function GetInPromptView({ pending, disabled, onGetIn }: { pending: "enter" | "exit" | null; disabled: boolean; onGetIn(): void }) {
  return (
    <div className="enter-prompt" role="group" aria-label="Car" data-testid="vehicle-prompt">
      <span className="small">
        <strong>Town car</strong>
      </span>
      <span className="small muted">Anyone can drive it. It stays on the roads.</span>
      <button
        type="button"
        className="btn btn-primary btn-lg"
        disabled={disabled}
        onClick={onGetIn}
        aria-label={pending === "enter" ? "Getting in the car" : "Get in the car (E)"}
        title={pending ? "Waiting for the server…" : "Sit in the driver's seat (E)"}
        data-testid="vehicle-get-in"
      >
        {pending === "enter" ? "Getting in…" : "Get in (E)"}
      </button>
    </div>
  );
}

export function DrivingHelpView({ isTouch }: { isTouch: boolean }) {
  return (
    <div className="mode-help" role="note" data-testid="driving-help">
      {isTouch
        ? "Driving: joystick up = gas, down = brake/reverse, sideways = steer · Get out at the top"
        : "Driving: W/↑ gas · S/↓ brake, then reverse · A/D or ←/→ steer · E get out"}
    </div>
  );
}
